import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage } from '../api/client';

export interface AsyncState<T> {
  data?: T;
  error?: string;
  loading: boolean;
  /** Re-run the loader (keeps the last data visible while reloading). */
  reload: () => void;
  /** Replace the data locally, e.g. after a mutation returned the new state. */
  setData: (next: T | undefined | ((prev: T | undefined) => T | undefined)) => void;
}

/**
 * Load data for a page. `deps` re-run the loader; a loader returning `undefined` clears the data.
 * Stale responses (from an earlier run) are ignored, so quick navigation never shows the wrong page.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const run = useRef(0);
  useEffect(() => {
    const id = ++run.current;
    setLoading(true);
    setError(undefined);
    loader().then(
      (d) => {
        if (id !== run.current) return;
        setData(d);
        setLoading(false);
      },
      (e: unknown) => {
        if (id !== run.current) return;
        setError(errorMessage(e));
        setLoading(false);
      },
    );
  }, [...deps, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload, setData };
}
